import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "./components/Layout.js";
import { Home } from "./pages/Home.js";
import { Profiles } from "./pages/Profiles.js";
import { Sources } from "./pages/Sources.js";
import { Library } from "./pages/Library.js";
import { Preferences } from "./pages/Preferences.js";
import { Login } from "./pages/Login.js";
import { Pair } from "./pages/Pair.js";
import { ClaimGoogle } from "./pages/ClaimGoogle.js";
import { Providers } from "./pages/Providers.js";
import { Onboarding } from "./pages/Onboarding.js";
import { Watch } from "./pages/Watch.js";
import { WatchYouTube } from "./pages/WatchYouTube.js";
import { History } from "./pages/History.js";
import { Forecourt } from "./pages/Forecourt.js";
import { Series } from "./pages/Series.js";
import { Service } from "./pages/Service.js";
import { Person } from "./pages/Person.js";
import { LiveNow } from "./pages/LiveNow.js";
import { WatchExternal } from "./pages/WatchExternal.js";
import { TVLobby } from "./pages/TVLobby.js";
import { Apps } from "./pages/Apps.js";
import { Wifi } from "./pages/Wifi.js";
import { RemotePair } from "./pages/RemotePair.js";
import { Remote } from "./pages/Remote.js";
import { Api } from "./lib/api.js";
import { getBearer, setBearer } from "./lib/auth.js";
import { getActiveProfileId, setActiveProfileId } from "./lib/profile.js";
// TV mode comes from lib/tvMode.ts — a leaf module so the ?tv=1 seeding runs
// before ANY page module body snapshots IS_TV (see that file for why). In TV
// mode the device is a dumb cast receiver: lobby screensaver, phone casts to it.
import { IS_TV } from "./lib/tvMode.js";

// The Pi is a TV receiver only — no browsing on it. A normal browser that
// lands on the Pi's public host (not in TV kiosk mode) is bounced to the cloud
// app, where all browsing/casting lives. The TV kiosk (IS_TV) is never
// redirected, so playback is unaffected. The cloud host serves the same bundle
// but its hostname doesn't match, so browsing there works normally.
(function redirectBrowsingToCloud() {
  if (typeof window === "undefined") return;
  const PI_HOSTS = ["wallacev.thinkingcap.com"];
  const CLOUD = "https://wallace-tv.thinkingcap.com";
  if (!IS_TV && PI_HOSTS.includes(window.location.hostname)) {
    window.location.replace(CLOUD + window.location.pathname + window.location.search + window.location.hash);
  }
})();

// If a ?token= param is present in the URL (e.g. from the cast QR code),
// store it as the bearer and strip it from the address bar.
// The kiosk launch URL rides the same channel for the rest of its config
// (?profile_id=, ?saver=, ?output=): a hard boot can wipe Chromium's
// localStorage (2026-09-20 — came up with no saver flag and a deleted
// profile), and the URL is the one seed that always survives.
(function bootstrapConfigFromUrl() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  let dirty = false;
  const t = params.get("token");
  if (t) { setBearer(t); params.delete("token"); dirty = true; }
  const pid = params.get("profile_id");
  if (pid && /^[0-9a-f-]{36}$/i.test(pid)) {
    try { setActiveProfileId(pid); } catch { /* private mode */ }
    params.delete("profile_id"); dirty = true;
  }
  const saver = params.get("saver");
  if (saver === "1" || saver === "0") {
    try { localStorage.setItem("wallace-tv:bandwidth-saver", saver); } catch { /* */ }
    params.delete("saver"); dirty = true;
  }
  const output = params.get("output");
  if (output === "wallace-tv" || output === "browser") {
    try { localStorage.setItem("wallace-tv-output-target", output); } catch { /* */ }
    params.delete("output"); dirty = true;
  }
  if (dirty) {
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? "?" + newSearch : "") + window.location.hash;
    window.history.replaceState(null, "", newUrl);
  }
})();

function uuid(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

// Stable client ID for browser heartbeat.
function getBrowserClientId(): string {
  const key = "wallace_browser_client_id";
  let id = localStorage.getItem(key);
  if (!id) { id = uuid(); localStorage.setItem(key, id); }
  return id;
}

export default function App(): JSX.Element {
  useLocation(); // subscribe to route changes so hasToken re-evaluates on navigation
  const hasToken = !!getBearer();
  // Ensure profile_id is in localStorage so every page (incl. BookmarkButton)
  // knows whose preferences to write against. Defaults to first profile.
  const profilesQ = useQuery({
    queryKey: ["profiles"],
    queryFn: Api.profiles.list,
    enabled: hasToken,
  });
  useEffect(() => {
    const list = profilesQ.data?.profiles;
    const first = list?.[0];
    if (!first) return;
    const stored = getActiveProfileId();
    // Seed when empty — and RESEED when the stored id no longer exists
    // (deleted profile): a stale id used to stick forever, 500ing every
    // /tv/progress heartbeat on the FK (kiosk, 2026-09-17).
    if (!stored || !list!.some((p) => p.id === stored)) {
      setActiveProfileId(first.id);
    }
  }, [profilesQ.data]);

  // Phone/browser heartbeat — tells the TV someone is browsing so it shows screensaver.
  useEffect(() => {
    if (!hasToken || IS_TV) return;
    const clientId = getBrowserClientId();
    const beat = () => Api.cast.clientHeartbeat(clientId).catch(() => {/* */});
    void beat();
    const t = window.setInterval(beat, 30_000);
    return () => window.clearInterval(t);
  }, [hasToken]);

  // ── TV MODE ─────────────────────────────────────────────────────────────
  // Must be checked BEFORE the auth gate — the TV kiosk has no stored token
  // when the Pi boots fresh (AP mode, WiFi not yet configured). Auth is not
  // meaningful on the TV; it's a dumb cast receiver.
  if (IS_TV) {
    return (
      <Routes>
        <Route path="/watch/yt/:videoId" element={<WatchYouTube />} />
        <Route path="/watch/external" element={<WatchExternal />} />
        <Route path="/watch/:id" element={<Watch />} />
        <Route path="*" element={<TVLobby />} />
      </Routes>
    );
  }

  if (!hasToken) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/pair" element={<Pair />} />
        <Route path="/claim" element={<ClaimGoogle />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // ── PHONE / BROWSER MODE ────────────────────────────────────────────────
  return (
    <Routes>
      <Route path="/pair" element={<Pair />} />
      <Route path="/claim" element={<ClaimGoogle />} />
      <Route path="/profiles/:id/onboarding" element={<Onboarding />} />
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/profiles" element={<Profiles />} />
        <Route path="/sources" element={<Sources />} />
        <Route path="/providers" element={<Providers />} />
        <Route path="/library" element={<Library />} />
        <Route path="/live" element={<LiveNow />} />
        <Route path="/series/:name" element={<Series />} />
        <Route path="/service/:key" element={<Service />} />
        <Route path="/person/:name" element={<Person />} />
        <Route path="/watch/yt/:videoId" element={<WatchYouTube />} />
        <Route path="/watch/external" element={<WatchExternal />} />
        <Route path="/watch/:id" element={<Watch />} />
        <Route path="/history" element={<History />} />
        <Route path="/favorites" element={<Forecourt />} />
        <Route path="/apps" element={<Apps />} />
        {/* Old URL kept so existing links continue working. */}
        <Route path="/forecourt" element={<Navigate to="/favorites" replace />} />
        <Route path="/preferences" element={<Preferences />} />
        <Route path="/wifi" element={<Wifi />} />
        <Route path="/remote" element={<Remote />} />
        <Route path="/remote-pair" element={<RemotePair />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
